// Split Bill Constants

import type { SplitBillContactsView } from "../interfaces/SplitBill";
import { TICK_ICON } from "./SuccessBanner";

// Split Bill Steps
export enum SplitBillStep {
    SPLIT_CONFIGURATION = "splitConfiguration",
    UPI_ENTRY = "upiEntry",
}

// Default values
export const DEFAULT_SHARE_VALUE = 1;
export const MIN_SHARE_VALUE = 1;
export const MAX_PERCENTAGE_VALUE = 100;

/** 100.00% as integer basis points (0.01% per unit; sum validates against this). */
export const PERCENTAGE_BASIS_POINTS_MAX = 10000;

// Identifiers
export const SELF_IDENTIFIER = "self";

/** Initial split-bill contact state before picker runs (guests only come from native bridge). */
export const EMPTY_SPLIT_BILL_CONTACTS: SplitBillContactsView = {
    self: { name: "", number: SELF_IDENTIFIER },
    guests: [],
};

// Native bridge `contactPicker` options for split bill
export const SPLIT_BILL_CONTACT_PICKER_CONFIG = {
    title: "Select contacts",
    ctaText: "Split bill",
    multiSelect: true,
    maxSelection: 50,
    source: "dineout_split_bill",
};

// Display text
export const BILL_AMOUNT_LABEL = "Bill amount";
export const GUESTS_LABEL = "Guests ({0})";
export const SPLIT_EQUALLY_LABEL = "Split equally";
export const SELF_DISPLAY_NAME = "You";
export const YOU_LABEL = "{0} (You)";
export const CONFIRM_BUTTON_TEXT = "Confirm";
export const ADD_MORE_GUESTS_TEXT = "Add more guests";

// Currency symbols
export const RUPEE_SYMBOL = "₹";
export const PERCENTAGE_SYMBOL = "%";

// ContactListItem constants
export const AVATAR_SIZE = "42px";
export const INPUT_WRAPPER_MAX_WIDTH = "80px";

// TextField constants
export const TEXT_FIELD_PADDING = "4px";

// UPI Step constants
export const UPI_STEP_HEADING = "Where can your friends pay?";
export const UPI_ID_LABEL = "Enter UPI ID (e.g., name@okaxis)";
export const UPI_HINT_TEXT = "Friends receive request via Whatsapp";
export const SEND_REQUEST_BUTTON_TEXT = "Send request";
export const UPI_ID_FIELD_NAME = "upiId";
export const UPI_LOTTIE_PATH = "portal/static-assets/lotties/upi_screen.json";
export const UPI_ID_REGEX = /^[\w.-]{3,}@[\w.-]{3,}$/;
export const UPI_ID_REQUIRED_ERROR = "Please enter your UPI ID";
export const UPI_ID_INVALID_ERROR = "Please enter a valid UPI ID";
export const UPI_USERNAME_REQUIRED_TOAST = "Please enter your user name first";

export const UPI_SUFFIX_CHIPS = [
    { label: "@ybl" },
    { label: "@okicici" },
    { label: "@okhdfcbank" },
    { label: "@paytm" },
    { label: "@upi" },
    { label: "@okaxis" },
    { label: "@axisb" },
    { label: "@oksbi" },
    { label: "@axl" },
    { label: "@ibl" },
    { label: "@apl" },
    { label: "@kotak" },
    { label: "@airtel" },
];

export const SPLIT_BILL_SUCCESS_OUTCOME = {
    imageId: TICK_ICON,
    title: "Wohoo! Request sent",
    subTitle: "WhatsApp messages sent to your friends with payment QR code",
    ctaLabel: "Great",
};

export const SPLIT_BILL_ERROR_OUTCOME = {
    imageId: "portal/static-assets/images/cross_image.png",
    title: "Oh no! unable to send request",
    subTitle: "Please retry to send the request to your guests",
    ctaLabel: "Retry",
};

/** Outcome illustration image size; matches outcome skeleton avatar shimmer. */
export const SPLIT_BILL_OUTCOME_IMAGE_SIZE = "46px";

/** Fixed width for the DineCash info tooltip in the split bill form. */
export const DINE_CASH_TOOLTIP_WRAPPER_WIDTH = "232px";

/** Full-width list inside the DineCash info tooltip. */
export const DINE_CASH_TOOLTIP_LIST_WIDTH = "100%";

export const SPLIT_BILL_ARIA_LABELS = {
    DINECASH_INFO: "Split DineCash info",
} as const;

/** Skeleton layout for split-bill status (`SplitBillStatusShimmer`); mirrors `SplitBillStatusView`. */
export const SPLIT_BILL_OUTCOME_SHIMMER = {
    image: {
        height: SPLIT_BILL_OUTCOME_IMAGE_SIZE,
        width: SPLIT_BILL_OUTCOME_IMAGE_SIZE,
        borderRadius: "50%",
    },
    titleLine: { height: "20px", width: "270px", borderRadius: "66px" },
    subtextLine1: { height: "10px", width: "270px", borderRadius: "6px" },
    subtextLine2: { height: "10px", width: "135px", borderRadius: "6px" },
    cta: { height: "52px", width: "100%", borderRadius: "16px" },
} as const;

// Validation messages
export const PERCENTAGE_VALIDATION_MESSAGE = "Percentage must add upto 100%";
export const AMOUNT_DIFFERENCE_LESS = "{0} less than total amount";
export const AMOUNT_DIFFERENCE_MORE = "{0} more than total amount";
export const ZERO_SPLIT_AMOUNT_MESSAGE = "Amount cannot be zero for any guest";

// Split bill image constants
export const SPLIT_BILL_IMAGE = {
    LEFT_LOGO_WIDTH: "34",
    LEFT_LOGO_HEIGHT: "16",
    RIGHT_LOGO_WIDTH: "14",
    RIGHT_LOGO_HEIGHT: "14",
};

export const DEFAULT_AVATAR_IMAGE_ID = "portal/static-assets/images/profile_icon.png";

// Timeout for contact picker native bridge call (60 seconds)
export const CONTACT_PICKER_TIMEOUT_MS = 60000;
